import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, TrendingUp, TrendingDown, Minus, AlertTriangle, Activity } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function scoreColor(score) {
  if (score == null) return '#6B7280';
  if (score >= 70) return '#16C784';
  if (score >= 45) return '#F59E0B';
  return '#EF4444';
}

function MiniBar({ score, colors }) {
  return (
    <div className="relative w-full" style={{ height: 6 }}>
      <div className="absolute inset-0 rounded-full overflow-hidden" style={{ backgroundColor: colors.progressTrack }}>
        <div
          className="h-full rounded-full"
          style={{
            width: `${score ?? 0}%`,
            background: 'linear-gradient(90deg, #EF4444 0%, #F59E0B 30%, #EAB308 55%, #22C55E 80%, #16C784 100%)',
            transition: 'width 0.8s ease',
          }}
        />
      </div>
      <div
        className="absolute top-1/2 -translate-y-1/2"
        style={{
          left: `calc(${score ?? 0}% - 5px)`,
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: '#fff',
          boxShadow: '0 0 4px rgba(0,0,0,0.6)',
          transition: 'left 0.8s ease',
          zIndex: 1,
        }}
      />
    </div>
  );
}

export default function TraderPerformancePortal() {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [bh, setBh] = useState(null);
  const [prepScore, setPrepScore] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([
      axios.get(`${API_URL}/behavioral-health`),
      axios.get(`${API_URL}/preparation/summary`),
    ])
      .then(([bhRes, prepRes]) => {
        if (!cancelled) {
          if (bhRes.status === 'fulfilled') setBh(bhRes.value.data);
          if (prepRes.status === 'fulfilled') setPrepScore(prepRes.value.data?.readiness?.score ?? null);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '1px', borderRadius: '16px', background: `linear-gradient(135deg, ${colors.accent}40, ${colors.accent}10 60%, ${colors.accent}30)` }}>
        <div className="rounded-2xl animate-pulse" style={{ background: `linear-gradient(135deg, ${colors.accent}12 0%, ${colors.accent}04 100%)` }}>
          <div className="flex items-center justify-between px-5 pt-4 pb-3" style={{ borderBottom: `1px solid ${colors.divider}` }}>
            <div className="h-2.5 w-36 rounded" style={{ backgroundColor: colors.skeletonBg }} />
            <div className="h-2.5 w-16 rounded" style={{ backgroundColor: colors.skeletonBg }} />
          </div>
          <div className="flex items-center gap-5 px-5 py-4">
            <div className="flex-shrink-0 text-center" style={{ minWidth: 80 }}>
              <div className="h-14 w-14 rounded-xl mx-auto" style={{ backgroundColor: colors.skeletonBg }} />
              <div className="h-2 w-10 rounded mx-auto mt-2" style={{ backgroundColor: colors.skeletonBgAlt }} />
            </div>
            <div style={{ width: 1, alignSelf: 'stretch', backgroundColor: colors.divider }} />
            <div className="flex-1 space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="space-y-1.5">
                  <div className="flex justify-between">
                    <div className="h-2 w-20 rounded" style={{ backgroundColor: colors.skeletonBg }} />
                    <div className="h-2 w-6 rounded" style={{ backgroundColor: colors.skeletonBg }} />
                  </div>
                  <div className="h-1.5 rounded-full" style={{ backgroundColor: colors.skeletonBgAlt }} />
                </div>
              ))}
            </div>
          </div>
          <div className="mx-4 mb-4 h-8 rounded-xl" style={{ backgroundColor: 'rgba(239,68,68,0.08)' }} />
        </div>
      </div>
    );
  }

  if (!bh) return null;

  const { final_score, discipline_score, personality, personality_state,
          personality_confidence, primary_issue, trend, risk, trades } = bh;
  const mainColor = scoreColor(final_score);
  const isPreLabel = personality_state === 'insufficient' || personality_state === 'mixed';

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendColor = trend === 'up' ? '#16C784' : trend === 'down' ? '#EF4444' : colors.secondaryText;
  const trendLabel = trend === 'up' ? 'Improving' : trend === 'down' ? 'Declining' : 'Stable';

  const pillars = [
    { label: 'Behavior',     score: discipline_score },
    { label: 'Risk',         score: risk?.adherence?.score ?? risk?.profile?.risk_score },
    { label: 'Results',      score: trades?.execution_score },
    { label: 'Preparation',  score: prepScore },
  ];

  return (
    <div
      style={{
        padding: '1px',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, rgba(139,92,246,0.22), rgba(139,92,246,0.07) 50%, rgba(139,92,246,0.18))',
        boxShadow: '0 0 12px rgba(139,92,246,0.05)',
        cursor: 'pointer',
      }}
      onClick={() => navigate('/trade-performance')}
    >
      <div className="rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.07) 0%, rgba(139,92,246,0.02) 100%)' }}>
        {/* Header */}
        <div
          className="flex items-center justify-between px-3 pt-2.5 pb-2 rounded-t-2xl"
          style={{ backgroundColor: 'rgba(139,92,246,0.06)', borderBottom: '1px solid rgba(139,92,246,0.12)' }}
        >
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(139,92,246,0.15)' }}>
              <Activity className="w-3.5 h-3.5" style={{ color: '#8b5cf6' }} />
            </div>
            <span className="text-[13px] font-semibold tracking-wider uppercase" style={{ color: colors.secondaryText }}>
              Trader Performance
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <TrendIcon className="w-3.5 h-3.5" style={{ color: trendColor }} />
            <span className="text-[11px] font-semibold" style={{ color: trendColor }}>{trendLabel}</span>
            <ChevronRight className="w-3.5 h-3.5" style={{ color: colors.secondaryText }} />
          </div>
        </div>

        {/* Body */}
        <div className="flex items-center gap-3 px-4 py-2">
          {/* Hero score */}
          <div className="flex-shrink-0 flex flex-col items-center" style={{ minWidth: 52 }}>
            <div className="font-bold leading-none tabular-nums" style={{ fontSize: 24, color: mainColor }}>
              {final_score}
            </div>
            <div className="text-[12px]" style={{ color: colors.secondaryText }}>/100</div>
            <div className="mt-1 px-1.5 py-0.5 rounded-full text-[11px] font-bold text-center"
              style={{ backgroundColor: `${mainColor}18`, color: mainColor, border: `1px solid ${mainColor}35` }}>
              {personality}
            </div>
            {!isPreLabel && personality_confidence > 0 && (
              <div className="text-[10px] mt-0.5" style={{ color: colors.secondaryText }}>{personality_confidence}% conf</div>
            )}
          </div>

          <div style={{ width: 1, alignSelf: 'stretch', backgroundColor: colors.dividerMed }} />

          <div className="flex-1 space-y-1.5">
            {pillars.map(({ label, score }) => {
              const c = scoreColor(score);
              return (
                <div key={label}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[13px]" style={{ color: colors.secondaryText }}>{label}</span>
                    <span className="text-[13px] font-bold tabular-nums" style={{ color: c }}>
                      {score ?? '—'}
                    </span>
                  </div>
                  <MiniBar score={score ?? 0} colors={colors} />
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-1.5 mx-3 mb-2.5 px-2.5 py-1.5 rounded-lg"
          style={{ backgroundColor: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.22)' }}>
          <AlertTriangle className="w-2.5 h-2.5 flex-shrink-0" style={{ color: '#EF4444' }} />
          <span className="text-[12px]" style={{ color: colors.secondaryText }}>Issue:</span>
          <span className="text-[12px] font-bold truncate" style={{ color: colors.primaryText }}>{primary_issue}</span>
        </div>
      </div>
    </div>
  );
}
